const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const SCHOOL_LABELS = {
  MMA: 'Mountainside',
  LSMA: 'Lakeside',
  TMA: 'Treeside',
  MOR: 'Morningside',
  PSMA: 'Parkside',
  DEN: 'Denton',
  TEM: 'Tempe',
  TUL: 'Tulsa',
};

const SCHOOL_ALIASES = Object.entries(SCHOOL_LABELS).reduce((aliases, [key, label]) => ({
  ...aliases,
  [key.toLowerCase()]: key,
  [label.toLowerCase()]: key,
}), {});

const normalize = (value) => String(value || '').trim().toLowerCase();

const normalizeSchool = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return SCHOOL_ALIASES[raw.toLowerCase()] || raw.toUpperCase();
};

const inferTimesheetMonth = (timesheet) => {
  const rawMonthYear = String(timesheet?.monthYear || '').trim();
  if (rawMonthYear) {
    const namedMatch = rawMonthYear.match(/^([a-z]+)[\s/-]+(\d{4})$/i);
    if (namedMatch) {
      const monthToken = namedMatch[1].toLowerCase();
      const monthIndex = MONTHS.findIndex((month) => (
        month === monthToken || month.slice(0, 3) === monthToken.slice(0, 3)
      ));
      const year = Number(namedMatch[2]);
      if (monthIndex >= 0 && Number.isInteger(year)) {
        return new Date(year, monthIndex, 1);
      }
    }

    const numericMatch = rawMonthYear.match(/^(\d{1,2})[/-](\d{4})$/);
    if (numericMatch) {
      const monthNumber = Number(numericMatch[1]);
      const year = Number(numericMatch[2]);
      if (monthNumber >= 1 && monthNumber <= 12 && Number.isInteger(year)) {
        return new Date(year, monthNumber - 1, 1);
      }
    }
  }

  const year = Number(timesheet?.year);
  const rawMonthNumber = Number(timesheet?.monthNumber ?? timesheet?.month);
  if (!Number.isInteger(year) || !Number.isInteger(rawMonthNumber)) return null;

  const monthIndex = rawMonthNumber >= 0 && rawMonthNumber <= 11
    ? rawMonthNumber
    : rawMonthNumber >= 1 && rawMonthNumber <= 12
      ? rawMonthNumber - 1
      : null;
  return monthIndex === null ? null : new Date(year, monthIndex, 1);
};

const buildTeacherSchoolLookup = (teachers = []) => {
  const lookup = new Map();
  (Array.isArray(teachers) ? teachers : []).forEach((teacher) => {
    const school = normalizeSchool(teacher?.School || teacher?.school);
    if (!school) return;
    const aliases = [
      teacher?.TeacherID,
      teacher?.teacherId,
      teacher?.Email,
      teacher?.email,
      teacher?.Name,
      teacher?.teacherName,
    ].map(normalize).filter(Boolean);
    aliases.forEach((alias) => lookup.set(alias, school));
  });
  return lookup;
};

const getTeacherKey = (timesheet) => {
  const key = [
    timesheet?.teacherId,
    timesheet?.TeacherID,
    timesheet?.email,
    timesheet?.teacherName,
  ].map(normalize).find(Boolean);
  return key || `timesheet:${String(timesheet?.timesheetId || timesheet?._id || '').trim()}`;
};

const getTimesheetSchool = (timesheet, teacherSchoolLookup) => {
  const directSchool = normalizeSchool(timesheet?.school || timesheet?.School);
  if (directSchool) return directSchool;

  const teacherAliases = [
    timesheet?.teacherId,
    timesheet?.TeacherID,
    timesheet?.email,
    timesheet?.teacherName,
  ].map(normalize).filter(Boolean);
  for (const alias of teacherAliases) {
    if (teacherSchoolLookup.has(alias)) return teacherSchoolLookup.get(alias);
  }

  const familySchool = (Array.isArray(timesheet?.families) ? timesheet.families : [])
    .map((family) => normalizeSchool(family?.school))
    .find(Boolean);
  return familySchool || '';
};

const getLessonKey = ({ lesson, family, teacherKey, monthKey }) => {
  const familyKey = String(family?.id || family?.familyWaveId || family?.familyName || '').trim();
  const lessonId = String(lesson?.id || lesson?.lessonId || '').trim();
  if (lessonId) return `${teacherKey}|${monthKey}|${familyKey}|id:${lessonId}`;

  return [
    teacherKey,
    monthKey,
    familyKey,
    normalize(lesson?.studentName),
    String(lesson?.date || '').trim(),
    String(lesson?.lessonLength || '').trim(),
  ].join('|');
};

const roundToTwo = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

export const buildTeacherHoursPerSchoolSeries = ({ timesheets = [], teachers = [] } = {}) => {
  const teacherSchoolLookup = buildTeacherSchoolLookup(teachers);
  const groups = new Map();

  (Array.isArray(timesheets) ? timesheets : []).forEach((timesheet) => {
    if (!timesheet || typeof timesheet !== 'object') return;
    const monthDate = inferTimesheetMonth(timesheet);
    const school = getTimesheetSchool(timesheet, teacherSchoolLookup);
    if (!monthDate || !school) return;

    const monthKey = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
    const groupKey = `${school}|${monthKey}`;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        school,
        schoolLabel: SCHOOL_LABELS[school] || school,
        monthKey,
        date: monthDate,
        teachers: new Map(),
      });
    }

    const teacherKey = getTeacherKey(timesheet);
    const group = groups.get(groupKey);
    if (!group.teachers.has(teacherKey)) {
      group.teachers.set(teacherKey, { minutes: 0, lessonKeys: new Set(), lessonCount: 0 });
    }
    const teacherEntry = group.teachers.get(teacherKey);

    (Array.isArray(timesheet?.families) ? timesheet.families : []).forEach((family) => {
      (Array.isArray(family?.lessons) ? family.lessons : []).forEach((lesson) => {
        const minutes = Number(lesson?.lessonLength);
        if (!Number.isFinite(minutes) || minutes <= 0 || minutes > 240) return;
        const lessonKey = getLessonKey({ lesson, family, teacherKey, monthKey });
        if (teacherEntry.lessonKeys.has(lessonKey)) return;

        teacherEntry.lessonKeys.add(lessonKey);
        teacherEntry.minutes += minutes;
        teacherEntry.lessonCount += 1;
      });
    });
  });

  return Array.from(groups.values())
    .map((group) => {
      const teachingTeachers = Array.from(group.teachers.values()).filter(
        (teacher) => teacher.minutes > 0
      );
      const totalMinutes = teachingTeachers.reduce((sum, teacher) => sum + teacher.minutes, 0);
      const lessonCount = teachingTeachers.reduce((sum, teacher) => sum + teacher.lessonCount, 0);
      const teacherCount = teachingTeachers.length;

      return {
        school: group.school,
        schoolLabel: group.schoolLabel,
        monthKey: group.monthKey,
        label: group.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        date: group.date,
        totalMinutes,
        totalHours: roundToTwo(totalMinutes / 60),
        averageHours: teacherCount ? roundToTwo(totalMinutes / 60 / teacherCount) : 0,
        teacherCount,
        lessonCount,
      };
    })
    .filter((row) => row.teacherCount > 0)
    .sort((left, right) => (
      left.date.getTime() - right.date.getTime()
      || left.schoolLabel.localeCompare(right.schoolLabel)
    ));
};
